import { useGetMapSnapshotMutation, useGetTargetsMutation } from 'app/api/facade';
import { setVesselsOnMap } from 'app/utils/facadeReducer';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { ViewportData } from 'ais-facade-api/api';
import { svgToDataURL } from 'app/helpers';
import { getVesselIcon, getAircraftIcon } from 'helpers/getWebGLPointIcon';
import { setHoveredTarget } from 'app/utils/mapStateSlice';
import { setRouteForSelectedVessel, setSelectedTarget } from 'app/utils/selectedTargetsSlice';
import { IconLayer, TextLayer } from '@deck.gl/layers/typed';
import { motionlessSpeed } from 'app/constants/_constants';
import { PickingInfo } from 'deck.gl/typed';

export const useLayerVessels = (
  showVessels: boolean,
  showIndicator: boolean,
  isHistoryMode: boolean,
  showVesselsNames: boolean,
  viewState: any,
) => {
  const dispatch = useAppDispatch();
  const mode = useAppSelector((state) => state.mapStateSlice.mode);

  const special = window._env_?.SPECIAL ? JSON.parse(window._env_.SPECIAL) : null;

  const isNotBaseMode = mode === 'analytics' || mode === 'playback';
  const isHistoryOrPlaybackMode = isHistoryMode || mode === 'playback';

  const [isBlinkingVisible, setBlinkingVisible] = useState<boolean>(true);
  const [hoverInfo, setHoverInfo] = useState<PickingInfo | null>(null);

  const [, { data: vesselsResponse, ...vesselsState }] = useGetTargetsMutation({ fixedCacheKey: 'get-map-vessels' });
  const [, { data: snapshotVesselsResponse, ...snapshotVesselsState }] = useGetMapSnapshotMutation({
    fixedCacheKey: 'get-map-snapshot-mutation',
  });

  const vessels = useAppSelector((state) => state.facadeSlice.vesselsOnMap);
  const selectedTarget = useAppSelector((state) => state.selectedTargetsSlice?.selectedTarget);
  const showRoute = useAppSelector((state) => state.selectedTargetsSlice.route.isShown);
  const rulerOn = useAppSelector((state) => state.mapStateSlice.tools.ruler);

  const getBlinkingSafetyIcon = (isVisible: boolean) =>
    `<svg class="safety-icon" width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
  <rect width="28" height="28" fill="none"/>
  <g transform="translate(7, 7)">
    <path d="M7 0.5C3.42386 0.5 0.5 3.42386 0.5 7C0.5 10.5761 3.42386 13.5 7 13.5C10.5761 13.5 13.5 10.5761 13.5 7C13.5 3.42386 10.5761 0.5 7 0.5ZM7 3.5C7.47978 3.5 7.97556 3.62807 8.45726 3.83563L7 5.29289L5.54274 3.83563C6.02444 3.62807 6.52022 3.5 7 3.5ZM3.5 7C3.5 6.52022 3.62807 6.02444 3.83563 5.54274L5.29289 7L3.83563 8.45726C3.62807 7.97556 3.5 7.47978 3.5 7ZM7 10.5C6.52022 10.5 6.02444 10.3719 5.54274 10.1644L7 8.70711L8.45726 10.1644C7.97556 10.3719 7.47978 10.5 7 10.5ZM10.5 7C10.5 7.47978 10.3719 7.97556 10.1644 8.45726L8.70711 7L10.1644 5.54274C10.3719 6.02444 10.5 6.52022 10.5 7Z" 
    fill="${isVisible ? '#FF0000' : 'none'}" stroke="white"/>
  </g>
</svg>`;

  const layerVessels = new IconLayer({
    id: 'layer-vessels',
    data: vessels,
    pickable: true,

    getPosition: (d: ViewportData): [number, number, number] => {
      if (typeof d?.lon === 'number' && typeof d?.lat === 'number') return [d.lon, d.lat, 0];
      return [999, 999, 0];
    },

    getAngle: (d) => {
      const speed = 'sog' in d ? d.sog : 'position' in d ? d.position?.sog : null;
      if (typeof speed === 'number' && speed < motionlessSpeed) return 0;

      return d.registrySpecialType === 'ATON' ? 0 : -d.heading || -d.cog;
    },
    getIcon: (d) => {
      if ('safety' in d && typeof d?.safety?.lastSignal === 'string') {
        return {
          url: svgToDataURL(getBlinkingSafetyIcon(isBlinkingVisible)),
          width: 28,
          height: 28,
        };
      }
      return {
        url: svgToDataURL(
          special !== null && special.includes(d.mmsi)
            ? getAircraftIcon()
            : getVesselIcon(d, showIndicator, isHistoryOrPlaybackMode),
        ),
        width: 28,
        height: 28,
      };
    },

    updateTriggers: {
      getIcon: [selectedTarget && selectedTarget.type === 'vessel' && null, showIndicator && vessels, isBlinkingVisible],
    },
    onClick(pickingInfo) {
      if (rulerOn) return;
      dispatch(
        setSelectedTarget({ target: pickingInfo, type: pickingInfo.object.registrySpecialType === 'ATON' ? 'aton' : 'vessel' }),
      );

      if (showRoute === false) {
        dispatch(
          setRouteForSelectedVessel({
            route: [],
            currentPoints: [],
            isShown: false,
          }),
        );
      }
    },
    onHover: (pickingInfo) => {
      setHoverInfo(pickingInfo);
      dispatch(setHoveredTarget(pickingInfo));
    },
    sizeMinPixels: 28,
    autoHighlight: true,
    visible: showVessels && !isNotBaseMode,
  });

  const layerVesselsNames = new TextLayer({
    id: 'layer-vessels-names',
    data: vessels.filter((d) => d !== hoverInfo?.object),
    getPosition: (d: ViewportData): [number, number, number] => {
      if (typeof d?.lon === 'number' && typeof d?.lat === 'number') return [d.lon, d.lat, 0];
      return [999, 999, 0];
    },

    getText: (d) => {
      return d.registryName ? d.registryName : d.name ? d.name : d.mmsi.toString();
    },
    getPixelOffset: () => {
      return [8, 0];
    },
    getColor: [255, 255, 255, 255],
    getSize: 10,
    sizeMinPixels: 10,
    sizeMaxPixels: 10,
    getAngle: 0,
    billboard: false,
    getTextAnchor: 'start',
    getAlignmentBaseline: 'bottom',
    visible: showVesselsNames && !isNotBaseMode && viewState.zoom > 6,
    pickable: false,
  });

  useEffect(() => {
    if (!isHistoryMode && vesselsResponse?.results && vesselsState.isSuccess) {
      dispatch(setVesselsOnMap(vesselsResponse?.results));
    }
    if (isHistoryMode && snapshotVesselsResponse?.results && snapshotVesselsState.isSuccess) {
      dispatch(setVesselsOnMap(snapshotVesselsResponse.results));
    }
  }, [vesselsState, snapshotVesselsState, isHistoryMode]);

  useEffect(() => {
    const interval = setInterval(() => {
      setBlinkingVisible((prev) => !prev);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return [layerVesselsNames, layerVessels];
};
